import React, { useState } from "react";
import Script from "dangerous-html/react";
import { Helmet } from "react-helmet";
import Navigation from "../components/navigation";
import Footer from "../components/footer";
import hotelConfig from "../lib/hotel.config";
import { supabase } from "../lib/supabase";
import "./home.css";

const Home = (props) => {
  const [lang, setLang] = useState("id");
  const [currentSlide, setCurrentSlide] = useState(0);
  const [kamars, setKamars] = useState([]);
  const [loadingKamars, setLoadingKamars] = useState(true);
  const [dbTestimoni, setDbTestimoni] = useState([]);
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });
  const [testimoniForm, setTestimoniForm] = useState({
    nama: "",
    komentar: "",
    rating: 5,
  });
  const [testimoniSubmitting, setTestimoniSubmitting] = useState(false);
  const [testimoniSuccess, setTestimoniSuccess] = useState(false);
  React.useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) =>
        prev === hotelConfig.foto_hero.length - 1 ? 0 : prev + 1,
      );
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  React.useEffect(() => {
    const fetchKamars = async () => {
      const { data } = await supabase
        .from("kamar")
        .select("*")
        .eq("status", "tersedia")
        .order("tipe");
      if (data) setKamars(data);
      setLoadingKamars(false);
    };
    fetchKamars();

    const channel = supabase
      .channel("kamar-realtime")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "kamar" },
        () => {
          fetchKamars();
        },
      )
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, []);

  React.useEffect(() => {
    const fetchTestimoni = async () => {
      const { data } = await supabase
        .from("testimoni")
        .select("*")
        .eq("status", "approved")
        .order("created_at", { ascending: false });
      if (data) setDbTestimoni(data);
    };
    fetchTestimoni();

    // Realtime subscription — auto update tanpa refresh
    const channel = supabase
      .channel("testimoni-changes")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "testimoni" },
        () => {
          fetchTestimoni();
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // Expose setLang globally so navigation script can call it
  React.useEffect(() => {
    window.__setLang = setLang;
    window.__getLang = () => lang;
  }, [lang]);

  const t = {
    // Hero
    hero_subtitle:
      lang === "id" ? hotelConfig.tagline.id : hotelConfig.tagline.en,
    booking_title: lang === "id" ? "Pesan Kamar Anda" : "Reserve Your Stay",
    checkin_label: lang === "id" ? "Tanggal Check-in" : "Check-in Date",
    checkout_label: lang === "id" ? "Tanggal Check-out" : "Check-out Date",
    guests_label: lang === "id" ? "Tamu" : "Guests",
    book_btn: lang === "id" ? "Pesan Sekarang" : "Book Now",
    guest_opt: (n) =>
      n === 1
        ? `1 ${lang === "id" ? "Tamu" : "Guest"}`
        : `${n} ${lang === "id" ? "Tamu" : "Guests"}`,

    // Highlights
    pickup_title: lang === "id" ? "Jemput & Antar" : "Pick-up & Drop",
    pickup_sub:
      lang === "id" ? "Transfer tersedia" : "Seamless airport transfers",
    parking_title: lang === "id" ? "Parkir Gratis" : "Free Parking",
    parking_sub: lang === "id" ? "Area parkir aman" : "Secure on-site space",
    wifi_title: lang === "id" ? "WiFi Gratis" : "Free WiFi",
    wifi_sub:
      lang === "id" ? "Koneksi berkecepatan tinggi" : "High-speed connectivity",
    service_title: lang === "id" ? "Layanan 24 Jam" : "24-Hour Service",
    service_sub:
      lang === "id" ? "Selalu siap untuk Anda" : "Always here for you",

    // About
    about_title:
      lang === "id" ? "Warisan Keanggunan" : "A Legacy of Refinement",
    about_body:
      lang === "id"
        ? `${hotelConfig.nama} memadukan keramahan Indonesia yang tradisional dengan kemewahan modern. Terletak di Fodo yang tenang, kami menawarkan pengalaman menginap di mana setiap detail dirancang untuk kenyamanan Anda.`
        : `${hotelConfig.nama} combines traditional Indonesian hospitality with modern luxury. Nestled in the serene landscapes of Fodo, we offer a stay where every detail is crafted for your utmost comfort.`,
    stat_rooms: lang === "id" ? "Kamar Mewah" : "Luxury Rooms",
    stat_service: lang === "id" ? "Layanan Ahli" : "Expert Service",
    stat_satisfaction: lang === "id" ? "Kepuasan Tamu" : "Guest Satisfaction",

    // Rooms
    rooms_title: lang === "id" ? "Layanan Kami" : "Our Service",
    rooms_subtitle:
      lang === "id"
        ? "Temukan koleksi kamar kami yang istimewa"
        : "Discover our collection of exquisite rooms",
    per_malam: lang === "id" ? "/malam" : "/night",
    mulai: lang === "id" ? "mulai" : "from",
    book_wa: lang === "id" ? "Pesan via WhatsApp" : "Book via WhatsApp",

    // Facilities
    facilities_title:
      lang === "id" ? "Fasilitas Kelas Dunia" : "World-Class Facilities",
    facilities_subtitle:
      lang === "id"
        ? "Semua yang Anda butuhkan untuk menginap yang tak terlupakan"
        : "Everything you need for an unforgettable stay",
    fac_wifi: lang === "id" ? "WiFi Berkecepatan Tinggi" : "High-Speed WiFi",
    fac_wifi_desc:
      lang === "id"
        ? "Tetap terhubung dengan internet fiber-optik di seluruh area hotel."
        : "Stay connected with fiber-optic internet throughout the resort.",
    fac_parking: lang === "id" ? "Parkir Aman" : "Secure Parking",
    fac_parking_desc:
      lang === "id"
        ? "Parkir gratis 24 jam dengan penjagaan untuk semua tamu."
        : "Complimentary 24/7 guarded parking for all our staying guests.",
    fac_pool: lang === "id" ? "Kolam Renang Infinity" : "Infinity Pool",
    fac_pool_desc:
      lang === "id"
        ? "Bersantai di kolam kami dengan pemandangan cakrawala yang menakjubkan."
        : "Relax in our temperature-controlled pool overlooking the horizon.",
    fac_spa: lang === "id" ? "Spa & Kesehatan" : "Spa & Wellness",
    fac_spa_desc:
      lang === "id"
        ? "Segarkan diri dengan pijat khas Indonesia yang menenangkan."
        : "Rejuvenate your senses with our signature Indonesian massage treatments.",
    fac_dining: lang === "id" ? "Restoran Fine Dining" : "Fine Dining",
    fac_dining_desc:
      lang === "id"
        ? "Cita rasa lokal dan internasional oleh chef terbaik kami."
        : "Authentic local flavors and international cuisine prepared by top chefs.",
    fac_gym: lang === "id" ? "Pusat Kebugaran" : "Fitness Center",
    fac_gym_desc:
      lang === "id"
        ? "Gym lengkap dengan peralatan kardio dan beban modern."
        : "Fully equipped gym with modern cardio and weight training facilities.",

    // Gallery
    gallery_labels:
      lang === "id"
        ? [
            "Eksterior Resort",
            "Pemandangan Sunset",
            "Suasana Malam",
            "Taman Hijau",
            "Paviliun Kolam",
          ]
        : [
            "Resort Exterior",
            "Sunset Views",
            "Night Ambiance",
            "Lush Gardens",
            "Pool Pavilion",
          ],

    // Testimonials
    testimonials_title: lang === "id" ? "Kata Tamu Kami" : "Guest Testimonials",
    testimonials_subtitle:
      lang === "id"
        ? "Kisah kemewahan dan kenyamanan dari tamu kami"
        : "Stories of luxury and comfort from our visitors",

    // Contact
    contact_title: lang === "id" ? "Hubungi Kami" : "Connect With Us",
    contact_subtitle:
      lang === "id"
        ? "Kami siap membantu pertanyaan dan pemesanan Anda"
        : "We are here to assist with your inquiries and bookings",
    contact_phone: lang === "id" ? "Telepon" : "Phone",
    contact_address: lang === "id" ? "Alamat" : "Address",
    contact_wa_btn:
      lang === "id"
        ? "Chat dengan kami di WhatsApp"
        : "Chat with us on WhatsApp",
  };

  const waUrl = (msg) => {
    const text =
      msg ||
      (lang === "id"
        ? `Halo, saya ingin memesan kamar di ${hotelConfig.nama}`
        : `Hello, I would like to book a room at ${hotelConfig.nama}`);
    return `https://wa.me/${hotelConfig.whatsapp}?text=${encodeURIComponent(text)}`;
  };

  const handleTestimoniSubmit = async () => {
    if (!testimoniForm.nama || !testimoniForm.komentar) return;
    setTestimoniSubmitting(true);
    await supabase.from("testimoni").insert({
      nama: testimoniForm.nama,
      komentar: testimoniForm.komentar,
      rating: testimoniForm.rating,
      status: "approved",
    });
    setTestimoniSuccess(true);
    setTestimoniForm({ nama: "", komentar: "", rating: 5 });
    setTestimoniSubmitting(false);
    const { data } = await supabase
      .from("testimoni")
      .select("*")
      .eq("status", "approved")
      .order("created_at", { ascending: false });
    if (data) setDbTestimoni(data);
  };

  const formatHarga = (harga) => `IDR ${harga.toLocaleString("id-ID")}`;

  const promoStart = new Date("2026-06-21");
  const promoEnd = new Date("2026-07-12T23:59:59");
  const now = new Date();
  const isPromo = now >= promoStart && now <= promoEnd;
  const diskon = 0.25;
  const hitungHargaPromo = (harga) => Math.round(harga * (1 - diskon));

  return (
    <div className="home-container1">
      <Helmet>
        <title>{hotelConfig.nama}</title>
        <meta property="og:title" content={hotelConfig.nama} />
      </Helmet>

      <Navigation lang={lang} setLang={setLang} />

      {/* ── HERO ── */}
      <section id="home" className="hero-booking">
        <div className="hero-booking__media">
          {hotelConfig.foto_hero.map((foto, idx) => (
            <img
              key={idx}
              alt={hotelConfig.nama}
              src={foto}
              className="hero-booking__img"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                opacity: idx === currentSlide ? 1 : 0,
                transition: "opacity 1s ease-in-out",
              }}
            />
          ))}
          <div
            className="hero-booking__overlay"
            style={{ background: "rgba(0,0,0,0.25)" }}
          ></div>
        </div>
        <div className="hero-booking__container">
          <div className="hero-booking__content">
            <h1 className="hero-title">
              Giga Owo Ori Hotel
              <span style={{ display: "block", textAlign: "center" }}>
                Pulau Nias
              </span>
            </h1>
            <p className="hero-subtitle">{t.hero_subtitle}</p>
          </div>
          <div className="hero-booking__panel">
            <div className="booking-card">
              <h2 className="section-title">{t.booking_title}</h2>
              <form
                id="hotel-booking-form"
                action="#"
                method="GET"
                className="booking-form"
              >
                <div className="booking-form__group">
                  <label className="booking-form__label">
                    {t.checkin_label}
                  </label>
                  <input
                    type="date"
                    id="checkin"
                    name="checkin"
                    required
                    className="booking-form__input"
                  />
                </div>
                <div className="booking-form__group">
                  <label className="booking-form__label">
                    {t.checkout_label}
                  </label>
                  <input
                    type="date"
                    id="checkout"
                    name="checkout"
                    required
                    className="booking-form__input"
                  />
                </div>
                <div className="booking-form__group">
                  <label className="booking-form__label">
                    {t.guests_label}
                  </label>
                  <select
                    id="guests"
                    name="jumlah_tamu"
                    required
                    className="booking-form__input"
                  >
                    {[1, 2, 3, 4, 5, 6].map((n) => (
                      <option key={n} value={n}>
                        {t.guest_opt(n)}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  id="thq_button_z4Rm"
                  name="button"
                  type="submit"
                  className="booking-form__btn btn-lg btn btn-primary"
                >
                  <span>{t.book_btn}</span>
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* ── HIGHLIGHTS BAR ── */}
      <section className="highlights-bar">
        <div className="highlights-bar__container">
          {hotelConfig.highlights.map((item, i) => (
            <div key={i} className="highlight-item">
              <div className="highlight-item__icon">
                {item.icon === "car" && (
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2" />
                    <circle r="2" cx="7" cy="17" />
                    <path d="M9 17h6" />
                    <circle r="2" cx="17" cy="17" />
                  </svg>
                )}
                {item.icon === "parking" && (
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="3" y="3" rx="2" width="18" height="18" />
                    <path d="M9 17V7h4a3 3 0 0 1 0 6H9" />
                  </svg>
                )}
                {item.icon === "wifi" && (
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 20h.01M2 8.82a15 15 0 0 1 20 0M5 12.859a10 10 0 0 1 14 0m-10.5 3.57a5 5 0 0 1 7 0" />
                  </svg>
                )}
                {item.icon === "clock" && (
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle r="10" cx="12" cy="12" />
                    <path d="M12 6v6l4 2" />
                  </svg>
                )}
                {item.icon === "restaurant" && (
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2" />
                    <path d="M7 2v20" />
                    <path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3zm0 0v7" />
                  </svg>
                )}
                {item.icon === "billiard" && (
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </div>
              <div className="highlight-item__text">
                <h3 className="section-content">{item.title[lang]}</h3>
                <p className="section-content highlight-item__sub">
                  {item.desc[lang]}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── ABOUT ── */}
      <section className="about-grid">
        <div className="about-grid__container">
          <div className="about-grid__text">
            <h2 className="section-title">{t.about_title}</h2>
            <p className="section-content">{t.about_body}</p>
            <div className="about-stats">
              <div className="stat-item">
                <span className="stat-item__number">
                  {hotelConfig.stats.kamar}
                </span>
                <span className="stat-item__label">{t.stat_rooms}</span>
              </div>
              <div className="stat-item">
                <span className="stat-item__number">
                  {hotelConfig.stats.layanan}
                </span>
                <span className="stat-item__label">{t.stat_service}</span>
              </div>
              <div className="stat-item">
                <span className="stat-item__number">
                  {Math.round(
                    (hotelConfig.testimoni.reduce(
                      (sum, t) => sum + t.rating,
                      0,
                    ) /
                      (hotelConfig.testimoni.length * 5)) *
                      100,
                  )}
                  %
                </span>
                <span className="stat-item__label">{t.stat_satisfaction}</span>
              </div>
            </div>
          </div>
          <div className="about-grid__visual">
            <div className="photo-grid">
              {hotelConfig.foto_about.map((foto, i) => (
                <div key={i} className="photo-grid__item">
                  <img src={foto} alt={`Hotel ${i + 1}`} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── ROOMS ── */}
      <section id="rooms" className="featured-rooms">
        <div className="featured-rooms__header">
          <h2 className="section-title">{t.rooms_title}</h2>
          <p className="section-subtitle">{t.rooms_subtitle}</p>
        </div>
        <div
          style={{
            maxWidth: "var(--content-max-width)",
            margin: "0 auto",
            padding: "0 var(--spacing-xl)",
            display: "flex",
            flexDirection: "column",
            gap: "1.5rem",
          }}
        >
          {loadingKamars ? (
            <div style={{ textAlign: "center", padding: "3rem", color: "#888" }}>
              Memuat data kamar...
            </div>
          ) : (() => {
            const tipeOrder = ["Superior Single", "Deluxe Twin", "Deluxe Single", "Executive", "President Suite"];
            const grouped = {};
            kamars.forEach((k) => {
              const tipeKey = k.tipe?.trim();
              if (!grouped[tipeKey]) grouped[tipeKey] = [];
              grouped[tipeKey].push(k);
            });
            const semuaTipe = Object.keys(grouped);
            const tipeList = [
              ...tipeOrder.filter((t) => grouped[t]),
              ...semuaTipe.filter((t) => !tipeOrder.includes(t)),
            ];
            if (tipeList.length === 0) {
              return (
                <div style={{ textAlign: "center", padding: "3rem", color: "#888" }}>
                  Belum ada kamar tersedia
                </div>
              );
            }
            return tipeList.map((tipe) => {
              const rep = grouped[tipe][0];
              const allFotos = grouped[tipe].flatMap((k) => k.foto_urls || []).filter(Boolean);
              const fotoUtama = allFotos[0] || "https://images.pexels.com/photos/2506990/pexels-photo-2506990.jpeg?auto=compress&cs=tinysrgb&w=1500";
              const hargaMulai = Math.min(...grouped[tipe].map((k) => k.harga_malam));
              const jumlahKamar = grouped[tipe].length;
              const ukuran = grouped[tipe].find((k) => k.ukuran)?.ukuran;
              return (
                <div
                  key={tipe}
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    borderRadius: "16px",
                    overflow: "hidden",
                    boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                    border: "1px solid #f0f0f0",
                    background: "white",
                    transition: "box-shadow 0.3s ease",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "0 6px 24px rgba(0,0,0,0.12)")}
                  onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,0,0,0.08)")}
                >
                  {/* Foto kiri */}
                  <div style={{ width: "280px", minWidth: "280px", overflow: "hidden" }}>
                    <img
                      src={fotoUtama}
                      alt={tipe}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  </div>

                  {/* Tengah */}
                  <div
                    style={{
                      flex: 1,
                      padding: "1.5rem",
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.5rem",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
                      <h3
                        style={{
                          fontFamily: "var(--font-family-heading)",
                          fontSize: "1.3rem",
                          fontWeight: "700",
                          color: "#1a1a1a",
                          margin: 0,
                        }}
                      >
                        {tipe}
                      </h3>
                      <span
                        style={{
                          fontSize: "12px",
                          fontWeight: "600",
                          color: "#dc2626",
                          backgroundColor: "#fef2f2",
                          padding: "4px 12px",
                          borderRadius: "20px",
                          border: "1px solid #fca5a5",
                        }}
                      >
                        {jumlahKamar} {lang === "id" ? "kamar tersedia" : "rooms available"}
                      </span>
                    </div>
                    <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap" }}>
                      <span style={{ fontSize: "13px", color: "#555" }}>
                        👤 {lang === "id" ? `Maks. ${rep.kapasitas} tamu` : `Max. ${rep.kapasitas} guests`}
                      </span>
                      <span style={{ fontSize: "13px", color: "#555" }}>
                        🏢 {lang === "id" ? `Lantai ${rep.lantai}` : `Floor ${rep.lantai}`}
                      </span>
                      {ukuran && (
                        <span style={{ fontSize: "13px", color: "#555" }}>📐 {ukuran} m²</span>
                      )}
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "0.25rem" }}>
                      {(rep.fasilitas || []).slice(0, 5).map((tag, j) => (
                        <span
                          key={j}
                          style={{
                            fontSize: "11px",
                            padding: "3px 10px",
                            backgroundColor: "#f0fafa",
                            color: "#0D8E8C",
                            borderRadius: "20px",
                            border: "1px solid #b2dfdb",
                            fontWeight: "500",
                          }}
                        >
                          ✓ {tag}
                        </span>
                      ))}
                    </div>
                    <div style={{ fontSize: "12px", color: "#27ae60", fontWeight: "600", marginTop: "0.25rem" }}>
                      ● {lang === "id" ? "Tersedia" : "Available"}
                    </div>
                  </div>

                  {/* Kanan */}
                  <div
                    style={{
                      width: "200px",
                      minWidth: "200px",
                      borderLeft: "1px solid #f0f0f0",
                      padding: "1.5rem",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      backgroundColor: "#fafffe",
                    }}
                  >
                    <div>
                      <div style={{ fontSize: "11px", color: "#888", marginBottom: "4px" }}>
                        {lang === "id" ? "Mulai dari" : "Starting from"}
                      </div>
                      {isPromo ? (
                        <>
                          <span
                            style={{
                              display: "inline-block",
                              fontSize: "10px",
                              fontWeight: "700",
                              color: "white",
                              backgroundColor: "#dc2626",
                              padding: "2px 7px",
                              borderRadius: "20px",
                              marginBottom: "4px",
                            }}
                          >
                            🎉 Promo 25%
                          </span>
                          <div style={{ fontSize: "13px", color: "#aaa", textDecoration: "line-through" }}>
                            IDR {hargaMulai.toLocaleString("id-ID")}
                          </div>
                          <div
                            style={{
                              fontSize: "22px",
                              fontWeight: "800",
                              color: "#0D8E8C",
                              fontFamily: "var(--font-family-heading)",
                              lineHeight: "1.2",
                            }}
                          >
                            IDR {hitungHargaPromo(hargaMulai).toLocaleString("id-ID")}
                          </div>
                          <div style={{ fontSize: "11px", color: "#dc2626", marginTop: "2px", fontWeight: "600" }}>
                            s.d. 12 Juli 2026
                          </div>
                        </>
                      ) : (
                        <>
                          <div
                            style={{
                              fontSize: "22px",
                              fontWeight: "800",
                              color: "#0D8E8C",
                              fontFamily: "var(--font-family-heading)",
                              lineHeight: "1.2",
                            }}
                          >
                            IDR {hargaMulai.toLocaleString("id-ID")}
                          </div>
                          <div style={{ fontSize: "11px", color: "#888", marginTop: "2px" }}>
                            {lang === "id" ? "per malam" : "per night"}
                          </div>
                        </>
                      )}
                    </div>
                    <button
                      onClick={() => (window.location.href = "/booking")}
                      style={{
                        width: "100%",
                        backgroundColor: "#0D8E8C",
                        color: "white",
                        border: "none",
                        borderRadius: "10px",
                        padding: "12px",
                        fontSize: "14px",
                        fontWeight: "700",
                        cursor: "pointer",
                        marginTop: "1rem",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#0a7a78")}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#0D8E8C")}
                    >
                      {lang === "id" ? "Pesan Kamar" : "Book Room"}
                    </button>
                  </div>
                </div>
              );
            });
          })()}
        </div>
      </section>

      {/* ── FACILITIES ── */}
      <section id="facilities" className="facilities-highlights">
        <div className="facilities-highlights__container">
          <div className="facilities-highlights__header">
            <h2 className="section-title">{t.facilities_title}</h2>
            <p className="section-subtitle">{t.facilities_subtitle}</p>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: "1.5rem",
            }}
          >
            {hotelConfig.highlights.map((fac, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "1rem",
                  padding: "1.5rem",
                  borderRadius: "16px",
                  border: "1px solid #f0ece4",
                  backgroundColor: "white",
                  transition: "box-shadow 0.3s ease",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.boxShadow =
                    "0 8px 24px rgba(0,0,0,0.08)")
                }
                onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "none")}
              >
                <div
                  style={{
                    width: "56px",
                    height: "56px",
                    flexShrink: 0,
                    borderRadius: "14px",
                    backgroundColor: "#0D8E8C",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {fac.icon === "car" && (
                    <svg
                      width="28"
                      height="28"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2" />
                      <circle r="2" cx="7" cy="17" />
                      <path d="M9 17h6" />
                      <circle r="2" cx="17" cy="17" />
                    </svg>
                  )}
                  {fac.icon === "parking" && (
                    <svg
                      width="28"
                      height="28"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="3" y="3" rx="2" width="18" height="18" />
                      <path d="M9 17V7h4a3 3 0 0 1 0 6H9" />
                    </svg>
                  )}
                  {fac.icon === "wifi" && (
                    <svg
                      width="28"
                      height="28"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M12 20h.01M2 8.82a15 15 0 0 1 20 0M5 12.859a10 10 0 0 1 14 0m-10.5 3.57a5 5 0 0 1 7 0" />
                    </svg>
                  )}
                  {fac.icon === "clock" && (
                    <svg
                      width="28"
                      height="28"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle r="10" cx="12" cy="12" />
                      <path d="M12 6v6l4 2" />
                    </svg>
                  )}
                  {fac.icon === "restaurant" && (
                    <svg
                      width="28"
                      height="28"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2" />
                      <path d="M7 2v20" />
                      <path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3zm0 0v7" />
                    </svg>
                  )}
                  {fac.icon === "billiard" && (
                    <svg
                      width="28"
                      height="28"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </div>
                <div>
                  <h3
                    style={{
                      fontSize: "1rem",
                      fontWeight: "700",
                      color: "#1a1a1a",
                      marginBottom: "0.4rem",
                      fontFamily: "var(--font-family-heading)",
                    }}
                  >
                    {fac.title[lang]}
                  </h3>
                  <p
                    style={{
                      fontSize: "13px",
                      color: "#666",
                      lineHeight: "1.6",
                      margin: 0,
                    }}
                  >
                    {fac.desc[lang]}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── GALLERY ── */}
      <section id="gallery" className="gallery-preview">
        <div style={{ textAlign: "center", padding: "3rem 0 2rem" }}>
          <p
            style={{
              fontSize: "12px",
              fontWeight: "600",
              letterSpacing: "0.15em",
              textTransform: "uppercase",
              color: "#D4AF00",
              marginBottom: "0.5rem",
            }}
          >
            {lang === "id" ? "Galeri" : "Gallery"}
          </p>
          <h2 className="section-title">
            {lang === "id" ? "Foto Hotel" : "Hotel Photos"}
          </h2>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gridAutoRows: "220px",
            gap: "6px",
            padding: "0 var(--spacing-xl)",
            maxWidth: "var(--content-max-width)",
            margin: "0 auto",
          }}
        >
          {hotelConfig.foto_galeri.map((foto, i) => (
            <div
              key={i}
              className="gallery-item"
              onClick={() => setLightbox({ open: true, index: i })}
              style={{
                gridColumn: i === 0 ? "span 2" : "span 1",
                gridRow: i === 0 ? "span 2" : "span 1",
                overflow: "hidden",
                borderRadius: "12px",
                cursor: "pointer",
              }}
            >
              <img
                src={foto}
                alt={t.gallery_labels[i] || `Foto ${i + 1}`}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  transition: "transform 0.5s ease",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.transform = "scale(1.08)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.transform = "scale(1)")
                }
              />
              <div className="gallery-item__overlay">
                <span className="section-content">
                  {t.gallery_labels[i] || `Foto ${i + 1}`}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Lightbox */}
        {lightbox.open && (
          <div
            onClick={() => setLightbox({ open: false, index: 0 })}
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0,0,0,0.92)",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {/* Tombol tutup */}
            <button
              onClick={() => setLightbox({ open: false, index: 0 })}
              style={{
                position: "absolute",
                top: "20px",
                right: "20px",
                background: "none",
                border: "none",
                color: "white",
                fontSize: "36px",
                cursor: "pointer",
                zIndex: 10000,
                lineHeight: 1,
              }}
            >
              ✕
            </button>

            {/* Tombol prev */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setLightbox((l) => ({
                  ...l,
                  index:
                    l.index === 0
                      ? hotelConfig.foto_galeri.length - 1
                      : l.index - 1,
                }));
              }}
              style={{
                position: "absolute",
                left: "16px",
                background: "rgba(255,255,255,0.15)",
                border: "none",
                color: "white",
                fontSize: "28px",
                cursor: "pointer",
                borderRadius: "50%",
                width: "48px",
                height: "48px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              ‹
            </button>

            {/* Foto */}
            <img
              src={hotelConfig.foto_galeri[lightbox.index]}
              alt={t.gallery_labels[lightbox.index]}
              onClick={(e) => e.stopPropagation()}
              style={{
                maxWidth: "92vw",
                maxHeight: "85vh",
                objectFit: "contain",
                borderRadius: "12px",
                boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
              }}
            />

            {/* Tombol next */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setLightbox((l) => ({
                  ...l,
                  index:
                    l.index === hotelConfig.foto_galeri.length - 1
                      ? 0
                      : l.index + 1,
                }));
              }}
              style={{
                position: "absolute",
                right: "16px",
                background: "rgba(255,255,255,0.15)",
                border: "none",
                color: "white",
                fontSize: "28px",
                cursor: "pointer",
                borderRadius: "50%",
                width: "48px",
                height: "48px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              ›
            </button>

            {/* Counter */}
            <div
              style={{
                position: "absolute",
                bottom: "20px",
                color: "rgba(255,255,255,0.7)",
                fontSize: "14px",
              }}
            >
              {lightbox.index + 1} / {hotelConfig.foto_galeri.length}
            </div>
          </div>
        )}
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className="testimonials">
        <div className="testimonials__container">
          <div className="testimonials__header">
            <h2 className="section-title">{t.testimonials_title}</h2>
            <p className="section-subtitle">{t.testimonials_subtitle}</p>
          </div>

          {/* Card testimoni */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
              gap: "1.5rem",
              marginBottom: "3rem",
            }}
          >
            {hotelConfig.testimoni.map((item, i) => (
              <div
                key={i}
                style={{
                  backgroundColor: "rgba(255,255,255,0.08)",
                  borderRadius: "16px",
                  padding: "1.75rem",
                  border: "1px solid rgba(255,255,255,0.1)",
                }}
              >
                <div
                  style={{ display: "flex", gap: "4px", marginBottom: "1rem" }}
                >
                  {[...Array(item.rating)].map((_, j) => (
                    <svg
                      key={j}
                      fill="var(--color-secondary)"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                  ))}
                </div>
                <blockquote
                  style={{
                    fontSize: "15px",
                    fontStyle: "italic",
                    color: "rgba(255,255,255,0.85)",
                    lineHeight: "1.7",
                    marginBottom: "1rem",
                  }}
                >
                  "{item.komentar[lang]}"
                </blockquote>
                <cite
                  style={{
                    color: "var(--color-secondary)",
                    fontWeight: "600",
                    fontSize: "14px",
                  }}
                >
                  — {item.nama}
                </cite>
              </div>
            ))}

            {/* Card testimoni dari Supabase */}
            {dbTestimoni.map((item, i) => (
              <div
                key={`db-${i}`}
                style={{
                  backgroundColor: "rgba(255,255,255,0.08)",
                  borderRadius: "16px",
                  padding: "1.75rem",
                  border: "1px solid rgba(255,255,255,0.1)",
                }}
              >
                <div
                  style={{ display: "flex", gap: "4px", marginBottom: "1rem" }}
                >
                  {[...Array(item.rating)].map((_, j) => (
                    <svg
                      key={j}
                      fill="var(--color-secondary)"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                  ))}
                </div>
                <blockquote
                  style={{
                    fontSize: "15px",
                    fontStyle: "italic",
                    color: "rgba(255,255,255,0.85)",
                    lineHeight: "1.7",
                    marginBottom: "1rem",
                  }}
                >
                  "{item.komentar}"
                </blockquote>
                <cite
                  style={{
                    color: "var(--color-secondary)",
                    fontWeight: "600",
                    fontSize: "14px",
                  }}
                >
                  — {item.nama}
                </cite>
              </div>
            ))}
          </div>

          {/* Form Testimoni */}
          <div
            style={{
              backgroundColor: "rgba(255,255,255,0.06)",
              borderRadius: "20px",
              padding: "2rem",
              border: "1px solid rgba(255,255,255,0.1)",
              maxWidth: "600px",
              margin: "0 auto",
            }}
          >
            <h3
              style={{
                color: "white",
                fontFamily: "var(--font-family-heading)",
                fontSize: "1.4rem",
                marginBottom: "0.5rem",
                textAlign: "center",
              }}
            >
              {lang === "id"
                ? "Bagikan Pengalaman Anda"
                : "Share Your Experience"}
            </h3>
            <p
              style={{
                color: "rgba(255,255,255,0.5)",
                fontSize: "13px",
                textAlign: "center",
                marginBottom: "1.5rem",
              }}
            >
              {lang === "id"
                ? "Ceritakan pengalaman menginap Anda di sini"
                : "Tell us about your stay experience"}
            </p>

            {/* Rating bintang */}
            <div style={{ textAlign: "center", marginBottom: "1.25rem" }}>
              <p
                style={{
                  color: "rgba(255,255,255,0.6)",
                  fontSize: "13px",
                  marginBottom: "0.5rem",
                }}
              >
                {lang === "id" ? "Rating Anda" : "Your Rating"}
              </p>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "8px",
                }}
              >
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() =>
                      setTestimoniForm((f) => ({ ...f, rating: star }))
                    }
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      fontSize: "28px",
                      color:
                        star <= testimoniForm.rating
                          ? "#D4AF00"
                          : "rgba(255,255,255,0.2)",
                      transition: "color 0.2s",
                    }}
                  >
                    ★
                  </button>
                ))}
              </div>
            </div>

            {/* Input nama */}
            <div style={{ marginBottom: "1rem" }}>
              <label
                style={{
                  color: "rgba(255,255,255,0.6)",
                  fontSize: "12px",
                  fontWeight: "600",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  display: "block",
                  marginBottom: "6px",
                }}
              >
                {lang === "id" ? "Nama Anda" : "Your Name"}
              </label>
              <input
                type="text"
                value={testimoniForm.nama}
                onChange={(e) =>
                  setTestimoniForm((f) => ({ ...f, nama: e.target.value }))
                }
                placeholder={
                  lang === "id" ? "Masukkan nama Anda" : "Enter your name"
                }
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  borderRadius: "10px",
                  border: "1px solid rgba(255,255,255,0.15)",
                  backgroundColor: "rgba(255,255,255,0.08)",
                  color: "white",
                  fontSize: "14px",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
            </div>

            {/* Input komentar */}
            <div style={{ marginBottom: "1.25rem" }}>
              <label
                style={{
                  color: "rgba(255,255,255,0.6)",
                  fontSize: "12px",
                  fontWeight: "600",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  display: "block",
                  marginBottom: "6px",
                }}
              >
                {lang === "id" ? "Komentar" : "Comment"}
              </label>
              <textarea
                value={testimoniForm.komentar}
                onChange={(e) =>
                  setTestimoniForm((f) => ({ ...f, komentar: e.target.value }))
                }
                placeholder={
                  lang === "id"
                    ? "Ceritakan pengalaman menginap Anda..."
                    : "Tell us about your stay..."
                }
                rows={4}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  borderRadius: "10px",
                  border: "1px solid rgba(255,255,255,0.15)",
                  backgroundColor: "rgba(255,255,255,0.08)",
                  color: "white",
                  fontSize: "14px",
                  outline: "none",
                  resize: "vertical",
                  boxSizing: "border-box",
                  fontFamily: "var(--font-family-body)",
                }}
              />
            </div>

            {/* Tombol submit */}
            <button
              onClick={handleTestimoniSubmit}
              disabled={testimoniSubmitting}
              style={{
                width: "100%",
                padding: "14px",
                borderRadius: "12px",
                backgroundColor: testimoniSubmitting ? "#888" : "#0D8E8C",
                color: "white",
                border: "none",
                fontSize: "15px",
                fontWeight: "700",
                cursor: testimoniSubmitting ? "not-allowed" : "pointer",
                transition: "background 0.2s",
              }}
            >
              {testimoniSubmitting
                ? lang === "id"
                  ? "Mengirim..."
                  : "Submitting..."
                : lang === "id"
                  ? "Kirim Testimoni"
                  : "Submit Review"}
            </button>

            {/* Pesan sukses */}
            {testimoniSuccess && (
              <div
                style={{
                  marginTop: "1rem",
                  padding: "12px",
                  borderRadius: "10px",
                  backgroundColor: "rgba(39,174,96,0.2)",
                  border: "1px solid rgba(39,174,96,0.4)",
                  color: "#27ae60",
                  textAlign: "center",
                  fontSize: "14px",
                }}
              >
                ✓{" "}
                {lang === "id"
                  ? "Testimoni berhasil dikirim! Terima kasih."
                  : "Review submitted successfully! Thank you."}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── CONTACT ── */}
      <section id="contact" className="contact-cta">
        <div className="contact-cta__container">
          <div className="contact-cta__header">
            <h2 className="section-title">{t.contact_title}</h2>
            <p className="section-subtitle">{t.contact_subtitle}</p>
          </div>
          <div className="contact-cards">
            <div className="contact-card">
              <div className="contact-card__icon">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M13.832 16.568a1 1 0 0 0 1.213-.303l.355-.465A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.468.351a1 1 0 0 0-.292 1.233a14 14 0 0 0 6.392 6.384" />
                </svg>
              </div>
              <h3 className="section-content">{t.contact_phone}</h3>
              <p className="section-content">{hotelConfig.telepon}</p>
            </div>
            <div className="contact-card">
              <div className="contact-card__icon">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m22 7l-8.991 5.727a2 2 0 0 1-2.009 0L2 7" />
                  <rect x="2" y="4" rx="2" width="20" height="16" />
                </svg>
              </div>
              <h3 className="section-content">Email</h3>
              <p className="section-content">{hotelConfig.email}</p>
            </div>
            <div className="contact-card">
              <div className="contact-card__icon">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
                  <circle r="3" cx="12" cy="10" />
                </svg>
              </div>
              <h3 className="section-content">{t.contact_address}</h3>
              <p className="section-content">{hotelConfig.alamat[lang]}</p>
            </div>
          </div>

          {/* Map Lokasi Hotel */}
          <div
            style={{
              margin: "0 0 var(--spacing-3xl)",
              borderRadius: "20px",
              overflow: "hidden",
              boxShadow: "0 8px 30px rgba(0,0,0,0.1)",
              border: "1px solid #f0ece4",
            }}
          >
            <div
              style={{
                background: "#0D8E8C",
                padding: "16px 24px",
                display: "flex",
                alignItems: "center",
                gap: "10px",
              }}
            >
              <span
                style={{ color: "white", fontWeight: "600", fontSize: "14px" }}
              >
                📍 {lang === "id" ? "Lokasi Hotel" : "Hotel Location"}
              </span>
            </div>
            <iframe
              src="https://maps.google.com/maps?q=1.2453545,97.6451617&z=16&output=embed"
              width="100%"
              height="380"
              style={{ border: 0, display: "block" }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Lokasi Giga Owo Ori Hotel"
            />
            <a
              href="https://www.google.com/maps/dir/?api=1&destination=1.2453545,97.6451617&travelmode=driving"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "block",
                width: "fit-content",
                margin: "12px auto 0",
                backgroundColor: "#0D8E8C",
                color: "white",
                padding: "10px 24px",
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: "600",
                textDecoration: "none",
              }}
            >
              🗺️ Buka & Navigasi di Google Maps
            </a>
          </div>

          <div className="contact-cta__action">
            <a href={waUrl()}>
              <div className="btn-accent btn-xl btn contact-whatsapp-btn">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m3 21l1.65-3.8a9 9 0 1 1 3.4 2.9z" />
                  <path d="M9 10a.5.5 0 0 0 1 0V9a.5.5 0 0 0-1 0za5 5 0 0 0 5 5h1a.5.5 0 0 0 0-1h-1a.5.5 0 0 0 0 1" />
                </svg>
                <span>{t.contact_wa_btn}</span>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* ── FLOATING WHATSAPP — bulat saja ── */}
      <a
        href={waUrl()}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat on WhatsApp"
      >
        <div className="floating-whatsapp">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
            <path d="M18.497 4.409a10 10 0 0 1-10.36 16.828l-.223-.098l-4.759.849l-.11.011a1 1 0 0 1-.11 0l-.102-.013l-.108-.024l-.105-.037l-.099-.047l-.093-.058l-.014-.011l-.012-.007l-.086-.073l-.077-.08l-.067-.088l-.056-.094l-.034-.07l-.04-.108l-.028-.128l-.012-.102a1 1 0 0 1 0-.125l.012-.1l.024-.11l.045-.122l1.433-3.304l-.009-.014A10 10 0 0 1 5.056 4.83l.215-.203a10 10 0 0 1 13.226-.217M9.5 7.5A1.5 1.5 0 0 0 8 9v1a6 6 0 0 0 6 6h1a1.5 1.5 0 0 0 0-3h-1l-.144.007a1.5 1.5 0 0 0-1.128.697l-.042.074l-.022-.007a4.01 4.01 0 0 1-2.435-2.435l-.008-.023l.075-.041A1.5 1.5 0 0 0 11 10V9a1.5 1.5 0 0 0-1.5-1.5" />
          </svg>
        </div>
      </a>

      {/* ── BOOKING LOGIC SCRIPT ── */}
      <div className="home-container2">
        <div className="home-container3">
          <Script
            html={`<script defer data-name="hotel-logic">
(function(){
  const bookingForm = document.getElementById("hotel-booking-form")
 if (bookingForm) {
    bookingForm.addEventListener("submit", (e) => {
      e.preventDefault()
      const checkin = document.getElementById("checkin").value
      const checkout = document.getElementById("checkout").value
      const guests = document.getElementById("guests").value
      const params = new URLSearchParams({
        checkin: checkin,
        checkout: checkout,
        jumlah_tamu: guests,
      })
      window.location.href = "/booking?" + params.toString()
    })
  }

  const checkinInput = document.getElementById("checkin")
  const checkoutInput = document.getElementById("checkout")
  if (checkinInput && checkoutInput) {
    const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1)
    const nextDay = new Date(tomorrow); nextDay.setDate(nextDay.getDate() + 2)
    checkinInput.value = tomorrow.toISOString().split("T")[0]
    checkoutInput.value = nextDay.toISOString().split("T")[0]
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.style.opacity = "1"
        entry.target.style.transform = "translateY(0)"
      }
    })
  }, { threshold: 0.1 })
  document.querySelectorAll("section").forEach((section) => {
  observer.observe(section)
})
})()
</script>`}
          />
        </div>
      </div>

      <Footer lang={lang} />
    </div>
  );
};

export default Home;
